import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { baiFetch } from "../auth";
import { minifyGraphQLQuery } from "../backendai";

export type ImageArchitecture = "x86_64" | "aarch64";

export interface Image {
  name: string;
  humanized_name: string;
  tag: string;
  registry: string;
  architecture: ImageArchitecture;
  digest: string;
  installed: boolean;
  resource_limits: {
    key: string;
    min: string;
    max: string;
  }[];
  labels: {
    key: string;
    value: string;
  }[];
}

export class ImageRef {
  constructor(
    private registry: string,
    private name: string,
    private tag: string,
    private architecture: ImageArchitecture
  ) {}

  static fromImage(image: Image): ImageRef {
    return new ImageRef(
      image.registry,
      image.name,
      image.tag,
      image.architecture
    );
  }

  get identifier(): string {
    return `${this.registry}/${this.name}:${this.tag}@${this.architecture}`;
  }
}

type ImageMetadata = {
  name: string;
  description: string;
  group: string;
  tags: string[];
  icon: string;
  label: {
    category?: string;
    tag: string;
    color: string;
  }[];
};

export const imageListAtom = atom(async (get) => {
  const response = await baiFetch("/func/admin/gql", {
    method: "POST",
    body: JSON.stringify({
      query: minifyGraphQLQuery(`
        query($installed: Boolean) {
          images(is_installed: $installed) {
            name
            humanized_name
            tag
            registry
            architecture
            digest
            installed
            resource_limits {
              key
              min
              max
            }
            labels {
              key
              value
            }
          }
        }
      `),
      variables: {
        installed:
          window.__RUNTIME_CONFIG__.FRONTEND_RUNTIME_NON_INSTALLED_IMAGES?.toLowerCase() ===
          "true"
            ? undefined
            : true,
      },
    }),
  }).then((res) => res.json());
  const images: Image[] = response.data.images;
  return images.filter(
    (image) =>
      !image.labels.some(
        (label) =>
          label.key === "ai.backend.features" &&
          label.value.split(" ").includes("private")
      )
  );
});

export const imageMetadataAtom = atom(async (get) => {
  const imageMetadataUrl = new URL(
    "/resources/image_metadata.json",
    window.__RUNTIME_CONFIG__.FRONTEND_RUNTIME_WEBSERVER_ENDPOINT
  );
  const response: {
    imageInfo: {
      [key: string]: ImageMetadata;
    };
    tagAlias: {
      [key: string]: string;
    };
    tagReplace: {
      [key: string]: string;
    };
  } = await fetch(imageMetadataUrl).then((res) => res.json());
  return response;
});

const STORAGE_KEY = {
  IMAGE: {
    USE_RAW_NAME: "Backend_AI_FastTrack.image.rawNameEnabled",
  },
};

export const isRawImageInputEnabledAtom = atomWithStorage<boolean>(
  STORAGE_KEY.IMAGE.USE_RAW_NAME,
  false,
  {
    getItem(key: string, initialValue: boolean) {
      const item = localStorage.getItem(key);
      if (item === null) {
        return initialValue;
      }
      return item.toLowerCase() === "true";
    },
    setItem(key: string, newValue: boolean | null) {
      localStorage.setItem(key, newValue?.toString().toLowerCase() ?? "false");
    },
    removeItem: localStorage.removeItem,
  },
  { getOnInit: true }
);
